import type { SVGProps } from 'react';

/**
 * File-format glyphs for the document surfaces (Word / PDF).
 *
 * Lucide has no format icons, and vendor logos (the Word "W" tile, the Adobe
 * PDF mark) would break the line-only rule in the design brief — so these are
 * drawn to match Lucide's geometry: a 24×24 box, a 2px round-capped stroke, and
 * the same folded-corner document silhouette used by `FileText`. The format is
 * spelled out in the sheet rather than implied by colour, which keeps them
 * legible in monochrome and at the 18px the menu renders them at.
 */
export interface FormatIconProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  /** Rendered edge length in px — matches the Lucide icons' `size` prop. */
  size?: number;
  strokeWidth?: number;
}

/** Shared document silhouette: a sheet with the top-right corner folded over. */
function Sheet({ strokeWidth }: { strokeWidth: number }) {
  return (
    <>
      <path
        d="M14.5 2.75H6.5A1.75 1.75 0 0 0 4.75 4.5v15A1.75 1.75 0 0 0 6.5 21.25h11a1.75 1.75 0 0 0 1.75-1.75V7.5Z"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.5 2.75V7.5h4.75"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  );
}

function FormatIcon({
  size = 24,
  strokeWidth = 2,
  label,
  fontSize,
  letterSpacing,
  ...rest
}: FormatIconProps & { label: string; fontSize: number; letterSpacing: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <Sheet strokeWidth={strokeWidth} />
      <text
        x="12"
        y="17.75"
        textAnchor="middle"
        fill="currentColor"
        stroke="none"
        fontFamily="'Manrope', sans-serif"
        fontSize={fontSize}
        fontWeight={700}
        letterSpacing={letterSpacing}
      >
        {label}
      </text>
    </svg>
  );
}

/** A Word document — the sheet marked with a bold "W". */
export function FileWord(props: FormatIconProps) {
  return <FormatIcon {...props} label="W" fontSize={8.5} letterSpacing={0} />;
}

/** A PDF document — the sheet marked with a condensed "PDF". */
export function FilePdf(props: FormatIconProps) {
  return <FormatIcon {...props} label="PDF" fontSize={6.25} letterSpacing={-0.35} />;
}
